import { useEffect, useRef, useState } from "react";
import { api, fmtErr } from "../lib/api";
import { ArrowsClockwise, Warning, SpinnerGap } from "@phosphor-icons/react";
import { toast } from "sonner";

const LABELS = {
  model_3d: "3D Model", print_drawing: "MFG Print", bom: "BOM",
  design_sheet: "Design Sheet", drawings: "2D Drawings",
};

export default function DownstreamSync({ project, onChanged }) {
  const [busy, setBusy] = useState(false);
  const pollRef = useRef(null);
  const ds = project?.downstream;

  useEffect(() => () => clearInterval(pollRef.current), []);

  if (!ds) return null;
  const stale = Object.keys(LABELS).filter((k) => ds[k]?.outdated);
  if (!stale.length) return null;

  const watchJob = (jobId) => {
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/drawings/${project.id}/job/${jobId}`);
        if (data.status === "done" || data.status === "failed" || data.status === "cancelled") {
          clearInterval(pollRef.current);
          setBusy(false);
          if (data.status === "done") toast.success(data.result?.summary || "Downstream artifacts regenerated");
          else toast.error(data.error?.message || "Sync job failed", { duration: 8000 });
          onChanged && onChanged();
        }
      } catch { /* keep polling */ }
    }, 3000);
  };

  const regenerateAll = async () => {
    setBusy(true);
    const done = [];
    try {
      if (ds.bom?.exists) {
        try { await api.post(`/bom/${project.id}/generate`); done.push("BOM"); }
        catch (e) { toast.error(`BOM: ${fmtErr(e)}`); }
      }
      if (ds.design_sheet?.exists) {
        try { await api.post(`/documents/${project.id}/design-sheet/generate`); done.push("design sheet"); }
        catch (e) { toast.error(`Design sheet: ${fmtErr(e)}`); }
      }
      const { data } = await api.post(`/drawings/${project.id}/generate`, { job_type: "sync_downstream" });
      toast.info(`BOM & design sheet regenerated. Drawings + 3D model + MFG print running as a background job…`);
      watchJob(data.job_id);
    } catch (e) {
      toast.error(fmtErr(e), { duration: 8000 });
      setBusy(false);
    }
  };

  return (
    <div className="mt-4 bg-[var(--c-amber)]/10 border border-[var(--c-amber)]/40 rounded-sm px-4 py-3 flex items-center gap-3 flex-wrap"
      data-testid="downstream-banner">
      <Warning size={16} weight="fill" className="text-[var(--c-amber)] shrink-0" />
      <span className="text-xs text-[var(--c-text)]">
        Design is at <b>v{(project.design || {}).active_version}</b> — these artifacts were built from an older version:
      </span>
      {stale.map((k) => (
        <span key={k} data-testid={`downstream-chip-${k}`}
          className="text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded-sm border border-[var(--c-amber)]/40 bg-[var(--c-amber)]/10 text-[var(--c-amber)]">
          {LABELS[k]}{k === "drawings" && ds.drawings.outdated_count ? ` (${ds.drawings.outdated_count})` : ""}
        </span>
      ))}
      <button data-testid="regenerate-affected-btn" onClick={regenerateAll} disabled={busy}
        className="ml-auto h-7 px-3 rounded-sm bg-[var(--c-amber)] text-black text-[11px] font-medium flex items-center gap-1.5 hover:opacity-90 disabled:opacity-60">
        {busy ? <SpinnerGap size={12} className="animate-spin" /> : <ArrowsClockwise size={12} weight="bold" />}
        {busy ? "Regenerating…" : "Regenerate all affected"}
      </button>
    </div>
  );
}
